import React, { useEffect, useState } from "react";
import ProtectedRoute from "./Common/ProtectedRoute";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  BrowserRouter as Router,
  BrowserRouter,
  Routes,
  Route,
  Link,
} from "react-router-dom";

import AccountAffiliates from "./Pages/Account/Affiliates";
import AccountApiKeys from "./Pages/Account/ApiKeys";
import AccountChangePassword from "./Pages/Account/ChangePassword";
import AccountIpWhiteListing from "./Pages/Account/IpWhitelisting";
import AccountOverview from "./Pages/Account/Overview";
import AccountSecurity from "./Pages/Account/Security";
import AccountVerification from "./Pages/Account/Verification";
import AccountVolumeDiscount from "./Pages/Account/VolumeDiscount";
import AccountWhitelistedDevices from "./Pages/Account/WhitelistedDevices";
import AccountExchangeToken from "./Pages/Account/ExchangeToken";
import AccountFirstVerification from "./Pages/Account/FirstVerification";
import { PageVisible } from "./constants/PageVisible";

import WalletAddressBook from "./Pages/Wallet/AddressBook";
import WalletBank from "./Pages/Wallet/Banks";
import WalletDeposits from "./Pages/Wallet/Deposits";
import WalletOverview from "./Pages/Wallet/Overview";
import WalletWithdrawal from "./Pages/Wallet/Withdrawals";
import ConvertFunds from "./Pages/Wallet/ConvertFunds";

import OrdersOpenOrders from "./Pages/Order/OpenOrders";
import OrdersOrderHistory from "./Pages/Order/OrderHistory";
import OrdersTradeHistory from "./Pages/Order/TradeHistory";

import ContentApiDocumentation from "./Pages/Content/ApiDocumentation";
import ContentCurrencies from "./Pages/Content/Currencies";
import ContentExchangeFees from "./Pages/Content/ExchangeFees";
import ContentAssetStatus from "./Pages/Content/AssetStatus";
import ContentTradingRules from "./Pages/Content/TradingRules";

import AdditionalInformationRequired from "./Pages/Content/AdditionalInformationRequired";

import MainHome from "./Pages/Main/Home";
import MainTrade from "./Pages/Main/Trade";
import MainEOtc from "./Pages/Main/E-OTC";
import MainSupport from "./Pages/Main/Support";

import AuthLogin from "./Pages/Auth/Login";
import AuthRegister from "./Pages/Auth/Register";
import AuthForgetPassword from "./Pages/Auth/ForgetPassword";
import AuthResetPassword from "./Pages/Auth/ResetPassword";
import AuthLoginVerification from "./Pages/Auth/LoginVerification";
import AuthLogout from "./Pages/Auth/Logout";

import NotFound from "./Pages/NotFound";

import Home from "./Pages/Main/Home";

import { useDispatch, useSelector } from "react-redux";
import { fetchLabels } from "./Features/Labels/LabelsSlice";

import SettingsModal from "./Components/SettingsModal";
import { useModalContext } from "./Context/ModalContext";
import Convert from "./Pages/Main/Convert";
import TempLogin from "./Pages/Auth/TempLogin";
import FiatPgResponse from "./Pages/Wallet/Withdrawals/FiatPgResponse";
import ConvertHistory from "./Pages/Order/ConvertHistory";
import {
  getCurrencySettings,
  getLanguageList,
  getSettings,
} from "./Features/Exchange/ExchangeSlice";
import { getProfile } from "./Features/User/UserSlice";
import { login } from "./Features/Session/SessionSlice";
import { getCryptoPrice, getFiatPrice } from "./Features/Price/PriceSlice";
import EarnCampaign from "./Pages/Main/Earn";
import EarnHistory from "./Pages/Main/Earn/History";
import EarnPlanDetails from "./Pages/Main/Earn/PlanDetails";

function App() {
  const [selectedMode, setSelectedMode] = useState("dark");
  const [profileLoaded, setProfileLoaded] = useState(false);
  const { selectedLanguage, selectedTheme, themeNo, setThemeNo } =
    useModalContext();
  const { isAuthenticated, userProfile } = useSelector((state) => state.user);
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSettings());
    dispatch(getLanguageList());
    dispatch(getCurrencySettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCryptoPrice());
    dispatch(getFiatPrice());

    const priceInterval = setInterval(() => {
      dispatch(getCryptoPrice());
      dispatch(getFiatPrice());
    }, 30000); // 30 seconds

    return () => {
      clearInterval(priceInterval);
    };
  }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userInfo = localStorage.getItem("userInfo");

    if (!profileLoaded && token) {
      if (userInfo && !userProfile) {
        try {
          const parsedUserInfo = JSON.parse(userInfo);

          dispatch(
            login({
              userToken: token,
              userProfile: userInfo,
            }),
          );
        } catch (error) {}
      }

      dispatch(getProfile())
        .then((result) => {
          const profileData = result?.payload?.data;
          if (profileData) {
            dispatch(
              login({
                userToken: token,
                userProfile: JSON.stringify(profileData),
              }),
            );
          }
          setProfileLoaded(true);
        })
        .catch((error) => {
          setProfileLoaded(true);
        });
    } else if (!token) {
      setProfileLoaded(true);
    }
  }, [dispatch, profileLoaded, userProfile]);

  useEffect(() => {
    if (selectedLanguage) {
      dispatch(fetchLabels(selectedLanguage.code));
    }
  }, [dispatch, selectedLanguage]);

  useEffect(() => {
    const themeNumber = process.env.REACT_APP_THEME_NO;
    setThemeNo(themeNumber ? parseInt(themeNumber) : 1);
  }, []);

  useEffect(() => {
    const mode = selectedTheme.name;
    setSelectedMode(mode);
  }, [selectedTheme]);

  useEffect(() => {
    const body = document.body;
    if (selectedTheme.name === "light") {
      body.classList.remove("dark");
      body.classList.add(`theme_no_${themeNo}`, "light");
    } else if (selectedTheme.name === "dark") {
      body.classList.remove("light");
      body.classList.add(`theme_no_${themeNo}`, "dark");
    }
  }, [selectedTheme]);

  return (
    <div>
      <BrowserRouter>
        <SettingsModal modalVisible={settingsModalVisible} />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<AuthLogin />} />
          <Route path="/register" element={<AuthRegister />} />
          <Route path="/forget-password" element={<AuthForgetPassword />} />
          <Route path="/reset-password/:otp" element={<AuthResetPassword />} />
          <Route
            path="/login-verification"
            element={<AuthLoginVerification />}
          />
          <Route
            path="/account-verification/:id"
            element={<AccountFirstVerification />}
          />
          <Route path="/logout" element={<AuthLogout />} />
          {PageVisible?.homePage && (
            <Route path="/home" element={<MainHome />} />
          )}
          {PageVisible?.eOtcPage && (
            <Route
              path="/e-otc"
              element={
                <ProtectedRoute>
                  <MainEOtc />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.earnCampaign && (
            <Route path="/earn" element={<EarnCampaign />} />
          )}
          {PageVisible?.earnCampaign && (
            <Route
              path="/earn-history"
              element={
                <ProtectedRoute>
                  <EarnHistory />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.earnCampaign && (
            <Route
              path="/earn-plan/:id"
              element={
                <ProtectedRoute>
                  <EarnPlanDetails />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.tradePage && (
            <Route path="/trade/:pair?" element={<MainTrade />} />
          )}
          <Route path="/redirect-customer" element={<TempLogin />} />
          <Route path="/fiat-pg-response/:token" element={<FiatPgResponse />} />

          {PageVisible?.convertPage && (
            <Route path="/convert" element={<Convert />} />
          )}
          <Route path="/support" element={<MainSupport />} />
          {PageVisible?.account_overviewPage && (
            <Route
              path="/account/overview"
              element={
                <ProtectedRoute>
                  <AccountOverview />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_verificationPage && (
            <Route
              path="/account/verification"
              element={
                <ProtectedRoute>
                  <AccountVerification />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_securityPage && (
            <Route
              path="/account/security"
              element={
                <ProtectedRoute>
                  <AccountSecurity />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_IPWhitelistPage && (
            <Route
              path="/account/ip-white-listing"
              element={
                <ProtectedRoute>
                  <AccountIpWhiteListing />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_AffiliatesPage && (
            <Route
              path="/account/affiliates"
              element={
                <ProtectedRoute>
                  <AccountAffiliates />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_ApiKeyPage && (
            <Route
              path="/account/api-keys"
              element={
                <ProtectedRoute>
                  <AccountApiKeys />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_ExchangeTokenPage && (
            <Route
              path="/account/exchange-token"
              element={
                <ProtectedRoute>
                  <AccountExchangeToken />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_VolumeDiscountPage && (
            <Route
              path="/account/volume-discount"
              element={
                <ProtectedRoute>
                  <AccountVolumeDiscount />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_ChangePasswordPage && (
            <Route
              path="/account/change-password"
              element={
                <ProtectedRoute>
                  <AccountChangePassword />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.account_WhitelistDevicesPage && (
            <Route
              path="/account/whitelisted-devices"
              element={
                <ProtectedRoute>
                  <AccountWhitelistedDevices />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_overviewPage && (
            <Route
              path="/wallet/overview"
              element={
                <ProtectedRoute>
                  <WalletOverview />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_depositPage && (
            <Route
              path="/wallet/deposits/"
              element={
                <ProtectedRoute>
                  <WalletDeposits />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_depositPage && (
            <Route
              path="/wallet/deposits/:currency"
              element={
                <ProtectedRoute>
                  <WalletDeposits />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_withdrawalPage && (
            <Route
              path="/wallet/withdrawals"
              element={
                <ProtectedRoute>
                  <WalletWithdrawal />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_withdrawalPage && (
            <Route
              path="/wallet/withdrawals/:currency"
              element={
                <ProtectedRoute>
                  <WalletWithdrawal />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_addressBookPage && (
            <Route
              path="/wallet/addressbook"
              element={
                <ProtectedRoute>
                  <WalletAddressBook />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_bankPage && (
            <Route
              path="/wallet/banks"
              element={
                <ProtectedRoute>
                  <WalletBank />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.wallet_convertDustPage && (
            <Route
              path="/wallet/convert-leftover-funds"
              element={
                <ProtectedRoute>
                  <ConvertFunds />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.order_openPage && (
            <Route
              path="/order/open-orders"
              element={
                <ProtectedRoute>
                  <OrdersOpenOrders />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.order_convertHistoryPage && (
            <Route
              path="/order/convert-orders"
              element={
                <ProtectedRoute>
                  <ConvertHistory />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.order_historyPage && (
            <Route
              path="/order/order-history"
              element={
                <ProtectedRoute>
                  <OrdersOrderHistory />
                </ProtectedRoute>
              }
            />
          )}
          {PageVisible?.order_tradeHistoryPage && (
            <Route
              path="/order/trade-history"
              element={
                <ProtectedRoute>
                  <OrdersTradeHistory />
                </ProtectedRoute>
              }
            />
          )}

          <Route
            path="/additional-information-required"
            element={<AdditionalInformationRequired />}
          />
          <Route
            path="/api-documentation"
            element={<ContentApiDocumentation />}
          />
          <Route path="/currencies" element={<ContentCurrencies />} />
          <Route path="/exchange-fees" element={<ContentExchangeFees />} />
          <Route path="/trading-rules" element={<ContentTradingRules />} />
          <Route path="/asset-status" element={<ContentAssetStatus />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={1}
      />
    </div>
  );
}

export default App;
